import React, { createContext, useContext, useState, useCallback } from 'react'
import { db, storage } from '../config/firebase'
import { collection, query, where, getDocs, addDoc, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore'
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'
import { fileCache } from '../lib/cache'
import { apiService } from '@/services/api'
import { USE_VPS_MEDIA } from '@/config/env'
import toast from 'react-hot-toast'

export const MediaContext = createContext({})

export const useMedia = () => {
  const context = useContext(MediaContext)
  if (!context) throw new Error('useMedia deve ser usado dentro de um MediaProvider')
  return context
}

const normalizeVpsMedia = (item) => ({
  id: String(item.id),
  user_id: item.user_id,
  organization_id: item.organization_id,
  workspace_id: item.workspace_id,
  name: item.original_name || item.filename || 'Mídia',
  title: item.original_name || item.filename || 'Mídia',
  size: item.file_size || 0,
  type: item.file_type || 'media',
  mimeType: item.mime_type,
  category: item.category || 'Uploads',
  created_at: item.created_at,
  createdAt: item.created_at,
  modified: item.updated_at || item.created_at,
  isShared: Boolean(item.is_shared),
  isFavorite: Boolean(item.is_favorite),
  provider: 'vps',
})

export const MediaProvider = ({ children }) => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [viewMode, setViewMode] = useState('grid')

  const loadFiles = useCallback(async (userId) => {
    try {
      setLoading(true)

      if (USE_VPS_MEDIA) {
        const payload = await apiService.getMedia({ limit: 100 })
        const baseItems = (payload.media || []).map(normalizeVpsMedia)
        const data = await Promise.all(baseItems.map(async (item) => {
          if (item.type !== 'image') return item
          try {
            const thumbnailUrl = await apiService.getProtectedMediaUrl(item.id, 'thumbnail')
            return { ...item, thumbnail: thumbnailUrl, thumbnailUrl }
          } catch {
            try {
              const url = await apiService.getProtectedMediaUrl(item.id, 'preview')
              return { ...item, url, thumbnail: url, thumbnailUrl: url }
            } catch {
              return item
            }
          }
        }))
        setFiles(data)
        setError(null)
        return { success: true, data }
      }

      const cachedFiles = fileCache.getList(userId)
      if (cachedFiles) setFiles(cachedFiles)

      const q = query(
        collection(db, 'media_files'),
        where('user_id', '==', userId)
      )

      const querySnapshot = await getDocs(q)
      const data = querySnapshot.docs.map((snapshot) => ({
        id: snapshot.id,
        ...snapshot.data()
      }))

      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

      setFiles(data || [])
      fileCache.setList(userId, data || [])
      setError(null)

      return { success: true, data: data || [] }
    } catch (err) {
      console.error('Erro ao carregar arquivos:', err)
      setError(err)
      toast.error('Erro ao carregar arquivos')
      return { success: false, error: err }
    } finally {
      setLoading(false)
    }
  }, [])

  const getFileById = useCallback(async (fileId) => {
    try {
      const localFile = files.find((f) => String(f.id) === String(fileId))
      if (localFile) return localFile

      if (USE_VPS_MEDIA) {
        const item = await apiService.getMediaById(fileId)
        if (!item) return null
        const normalized = normalizeVpsMedia(item)
        try {
          const url = await apiService.getProtectedMediaUrl(fileId, normalized.type === 'image' ? 'preview' : 'stream')
          return { ...normalized, url, thumbnail: normalized.type === 'image' ? url : normalized.thumbnail }
        } catch {
          return normalized
        }
      }

      const docRef = doc(db, 'media_files', fileId)
      const docSnap = await getDoc(docRef)

      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null
    } catch (err) {
      console.error('Erro ao buscar arquivo:', err)
      return null
    }
  }, [files])

  const uploadFile = useCallback(async (file, userId, onProgress) => {
    try {
      setUploading(true)

      if (USE_VPS_MEDIA) {
        const payload = await apiService.uploadMedia(file, onProgress)
        const uploaded = (payload.files || []).map((item) => normalizeVpsMedia({
          id: item.id,
          filename: item.filename,
          original_name: item.originalName,
          file_size: item.fileSize,
          mime_type: item.mimeType,
          file_type: item.fileType,
          category: item.category,
          created_at: new Date().toISOString(),
        }))

        if (uploaded.length) {
          const hydrated = await Promise.all(uploaded.map(async (item) => {
            if (item.type !== 'image') return item
            try {
              const thumbnailUrl = await apiService.getProtectedMediaUrl(item.id, 'thumbnail')
              return { ...item, thumbnail: thumbnailUrl, thumbnailUrl }
            } catch {
              return item
            }
          }))
          setFiles((prev) => [...hydrated, ...prev])
        }
        toast.success('Arquivo enviado com sucesso!')
        return { success: true, data: uploaded[0] || null }
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const storageRef = ref(storage, `users/${userId}/${Date.now()}-${safeName}`)
      const uploadTask = uploadBytesResumable(storageRef, file)

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (onProgress) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              onProgress(progress)
            }
          },
          reject,
          resolve
        )
      })

      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

      const fileData = {
        user_id: userId,
        name: file.name,
        size: file.size,
        type: file.type,
        url: downloadURL,
        storage_path: storageRef.fullPath,
        created_at: new Date().toISOString()
      }

      const docRef = await addDoc(collection(db, 'media_files'), fileData)
      const newFile = { id: docRef.id, ...fileData }

      setFiles((prev) => [newFile, ...prev])
      fileCache.setList(userId, [newFile, ...files])

      toast.success('Arquivo enviado com sucesso!')
      return { success: true, data: newFile }
    } catch (err) {
      console.error('Erro no upload:', err)
      toast.error('Erro ao enviar arquivo: ' + err.message)
      return { success: false, error: err }
    } finally {
      setUploading(false)
    }
  }, [files])

  const deleteFile = useCallback(async (fileId) => {
    try {
      if (USE_VPS_MEDIA) {
        await apiService.deleteMedia(fileId)
        setFiles((prev) => prev.filter((f) => String(f.id) !== String(fileId)))
        toast.success('Arquivo excluído com sucesso')
        return { success: true }
      }

      const file = await getFileById(fileId)
      if (!file) throw new Error('Arquivo não encontrado')

      if (file.storage_path) {
        const storageRef = ref(storage, file.storage_path)
        await deleteObject(storageRef).catch((err) => {
          console.warn('Erro ao deletar do storage (pode não existir):', err)
        })
      }

      await deleteDoc(doc(db, 'media_files', fileId))
      setFiles((prev) => prev.filter((f) => f.id !== fileId))

      toast.success('Arquivo excluído com sucesso')
      return { success: true }
    } catch (err) {
      console.error('Erro ao deletar arquivo:', err)
      toast.error('Erro ao excluir arquivo')
      return { success: false, error: err }
    }
  }, [getFileById])

  const updateFile = useCallback(async (fileId, data) => {
    try {
      if (USE_VPS_MEDIA) {
        const mapped = {
          category: data.category,
          is_favorite: data.is_favorite ?? data.isFavorite,
        }
        await apiService.updateMedia(fileId, mapped)
        setFiles((prev) => prev.map((f) => String(f.id) === String(fileId) ? { ...f, ...data } : f))
        toast.success('Arquivo atualizado com sucesso')
        return { success: true }
      }

      const docRef = doc(db, 'media_files', fileId)
      await updateDoc(docRef, data)
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, ...data } : f))
      toast.success('Arquivo atualizado com sucesso')
      return { success: true }
    } catch (err) {
      console.error('Erro ao atualizar arquivo:', err)
      toast.error('Erro ao atualizar arquivo')
      return { success: false, error: err }
    }
  }, [])

  return (
    <MediaContext.Provider value={{
      files,
      loading,
      error,
      uploading,
      searchQuery,
      setSearchQuery,
      filterType,
      setFilterType,
      viewMode,
      setViewMode,
      loadFiles,
      uploadFile,
      deleteFile,
      updateFile,
      getFileById,
      mediaProvider: USE_VPS_MEDIA ? 'vps' : 'firebase',
    }}>
      {children}
    </MediaContext.Provider>
  )
}

export default MediaContext
